import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './pagination.module.scss';

const buildPageItems = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'dots', total];
  }

  if (current >= total - 3) {
    return [1, 'dots', total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, 'dots', current - 1, current, current + 1, 'dots', total];
};

const Pagination = ({ page, pageCount, onChange, loading }) => {
  if (pageCount <= 1) {
    return null;
  }

  const items = buildPageItems(page, pageCount);
  const handleChange = nextPage => {
    if (loading || nextPage === page) {
      return;
    }

    onChange(nextPage);
  };

  return (
    <div
      className={styles.pagination}
      role="navigation"
      aria-label="Pagination"
    >
      <button
        type="button"
        className={cn(
          styles.pageButton,
          styles.arrow,
          (page === 1 || loading) && styles.disabled
        )}
        onClick={() => handleChange(Math.max(1, page - 1))}
        disabled={page === 1 || loading}
        aria-label="Previous page"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M14.4698 5.46973C14.7627 5.17683 15.2374 5.17683 15.5303 5.46973C15.8232 5.76262 15.8232 6.2374 15.5303 6.53027L10.0606 12L15.5303 17.4697C15.8232 17.7626 15.8232 18.2374 15.5303 18.5303C15.2375 18.8231 14.7627 18.8231 14.4698 18.5303L8.46979 12.5303C8.17689 12.2374 8.17689 11.7626 8.46979 11.4697L14.4698 5.46973Z"
            fill="currentColor"
          />
        </svg>
      </button>
      {items.map((item, index) =>
        item === 'dots' ? (
          <span key={`dots-${index}`} className={styles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            type="button"
            key={item}
            className={cn(
              styles.pageButton,
              item === page && styles.active,
              loading && styles.disabled
            )}
            onClick={() => handleChange(item)}
            disabled={loading}
            aria-current={item === page ? 'page' : undefined}
          >
            {item}
          </button>
        )
      )}
      <button
        type="button"
        className={cn(
          styles.pageButton,
          styles.arrow,
          (page === pageCount || loading) && styles.disabled
        )}
        onClick={() => handleChange(Math.min(pageCount, page + 1))}
        disabled={page === pageCount || loading}
        aria-label="Next page"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M8.46973 5.46973C8.76262 5.17683 9.23738 5.17683 9.53027 5.46973L15.5303 11.4697C15.8231 11.7626 15.8231 12.2374 15.5303 12.5303L9.53027 18.5303C9.2374 18.8231 8.76262 18.8231 8.46973 18.5303C8.17683 18.2374 8.17683 17.7626 8.46973 17.4697L13.9395 12L8.46973 6.53027C8.17683 6.23738 8.17683 5.76262 8.46973 5.46973Z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

Pagination.defaultProps = {
  loading: false,
};

export default Pagination;
